/**
 * Draft autosave (§16.1 "templates and draft autosave"): debounced PATCH with optimistic concurrency
 * (`expectedConfigVersion`); `version_conflict` surfaces as a conflict the operator resolves explicitly.
 * A new lottery is created with the first save and the URL switches to `/lotteries/:id/edit`.
 */
import { useQueryClient } from '@tanstack/react-query';
import type { LotteryAdminDto } from '@windrop/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { lotteriesApi } from '../../../lib/api/admin';
import { isApiError } from '../../../lib/api/client';
import { DraftConfigSchema, toDraftInput, type DraftConfigInput } from './steps';

export type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'dirty' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: Date; version: number }
  | { kind: 'invalid'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'conflict'; message: string };

export interface DraftAutosaveOptions {
  form: UseFormReturn<DraftConfigInput>;
  lotteryId: string | null;
  /** Version the editor loaded; null for a new draft. */
  initialVersion: number | null;
  hasEntries: boolean;
  changeNote?: string;
  participantRemedy?: string;
  debounceMs?: number;
  onCreated?: (lottery: LotteryAdminDto) => void;
  enabled?: boolean;
}

export interface DraftAutosave {
  status: SaveStatus;
  version: number | null;
  saveNow: () => Promise<void>;
  /** Conflict resolution: discard local edits and load the server copy. */
  reloadServerCopy: () => Promise<void>;
  /** Conflict resolution: write local edits over the newer server version. */
  overwriteServerCopy: () => Promise<void>;
}

export function useDraftAutosave({
  form,
  lotteryId,
  initialVersion,
  hasEntries,
  changeNote,
  participantRemedy,
  debounceMs = 1500,
  onCreated,
  enabled = true,
}: DraftAutosaveOptions): DraftAutosave {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SaveStatus>({ kind: 'idle' });
  const [version, setVersion] = useState<number | null>(initialVersion);
  const lastSaved = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const inFlight = useRef<Promise<void> | null>(null);
  const idRef = useRef(lotteryId);
  idRef.current = lotteryId;
  const versionRef = useRef(version);
  versionRef.current = version;

  const persist = useCallback(
    async (expectedVersion: number | null) => {
      const values = form.getValues();
      const parsed = DraftConfigSchema.safeParse(values);
      if (!parsed.success) {
        const first = parsed.error.issues[0];
        setStatus({
          kind: 'invalid',
          message: `Not saved: ${first?.path.join('.') ?? 'config'} — ${first?.message ?? 'invalid'}`,
        });
        return;
      }
      const serialized = JSON.stringify(parsed.data);
      if (serialized === lastSaved.current) {
        setStatus((s) => (s.kind === 'dirty' ? { kind: 'idle' } : s));
        return;
      }
      setStatus({ kind: 'saving' });
      try {
        let saved: LotteryAdminDto;
        if (!idRef.current) {
          saved = await lotteriesApi.create({ config: parsed.data });
          idRef.current = saved.lotteryId;
          onCreated?.(saved);
        } else {
          saved = await lotteriesApi.update(idRef.current, {
            expectedConfigVersion: expectedVersion ?? versionRef.current ?? 1,
            config: parsed.data,
            changeNote: hasEntries ? changeNote : undefined,
            participantRemedy: hasEntries ? participantRemedy : undefined,
          });
        }
        lastSaved.current = JSON.stringify(toDraftInput(saved.config));
        setVersion(saved.configVersion);
        versionRef.current = saved.configVersion;
        queryClient.setQueryData(['lottery', saved.lotteryId], saved);
        setStatus({ kind: 'saved', at: new Date(), version: saved.configVersion });
      } catch (e) {
        if (isApiError(e) && e.code === 'version_conflict') {
          setStatus({ kind: 'conflict', message: e.message });
        } else if (isApiError(e) && e.code === 'config_locked') {
          setStatus({
            kind: 'error',
            message: `${e.message} Add a change note and participant remedy on the Review step.`,
          });
        } else {
          setStatus({
            kind: 'error',
            message: isApiError(e) ? e.message : 'Autosave failed; your edits are still here.',
          });
        }
      }
    },
    [form, hasEntries, changeNote, participantRemedy, onCreated, queryClient],
  );

  const saveNow = useCallback(async () => {
    clearTimeout(timer.current);
    const p = persist(null);
    inFlight.current = p;
    await p;
    inFlight.current = null;
  }, [persist]);

  // Seed `lastSaved` with the loaded values so an untouched form never saves.
  useEffect(() => {
    if (lotteryId && lastSaved.current === null) {
      const parsed = DraftConfigSchema.safeParse(form.getValues());
      if (parsed.success) lastSaved.current = JSON.stringify(parsed.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotteryId]);

  useEffect(() => {
    if (!enabled) return;
    const sub = form.watch(() => {
      setStatus((s) => (s.kind === 'conflict' || s.kind === 'saving' ? s : { kind: 'dirty' }));
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setStatus((s) => {
          if (s.kind === 'conflict') return s;
          void persist(null);
          return s;
        });
      }, debounceMs);
    });
    return () => {
      sub.unsubscribe();
      clearTimeout(timer.current);
    };
  }, [form, persist, debounceMs, enabled]);

  const reloadServerCopy = useCallback(async () => {
    if (!idRef.current) return;
    const fresh = await lotteriesApi.get(idRef.current);
    form.reset(toDraftInput(fresh.config));
    lastSaved.current = JSON.stringify(toDraftInput(fresh.config));
    setVersion(fresh.configVersion);
    versionRef.current = fresh.configVersion;
    queryClient.setQueryData(['lottery', fresh.lotteryId], fresh);
    setStatus({ kind: 'saved', at: new Date(), version: fresh.configVersion });
  }, [form, queryClient]);

  const overwriteServerCopy = useCallback(async () => {
    if (!idRef.current) return;
    const fresh = await lotteriesApi.get(idRef.current);
    await persist(fresh.configVersion);
  }, [persist]);

  return { status, version, saveNow, reloadServerCopy, overwriteServerCopy };
}
